import { useState } from 'react'

const MCP_ENDPOINT = 'https://legali-docs.pages.dev/api/mcp'

interface InstallConfig {
    vscode: string
    claudeCode: string
    claudeDesktop: string
}

function generateConfigs(): InstallConfig {
    const vsCodeConfig = {
        name: 'legali-docs',
        type: 'http',
        url: MCP_ENDPOINT,
    }

    return {
        vscode: `vscode:mcp/install?${encodeURIComponent(JSON.stringify(vsCodeConfig))}`,
        claudeCode: `claude mcp add legali-docs --transport http --url "${MCP_ENDPOINT}"`,
        claudeDesktop: JSON.stringify({
            mcpServers: {
                'legali-docs': {
                    transport: 'http',
                    url: MCP_ENDPOINT,
                },
            },
        }, null, 2),
    }
}

export function MCPInstall() {
    const [activeTab, setActiveTab] = useState<'vscode' | 'claude-code' | 'claude-desktop'>('vscode')
    const [copied, setCopied] = useState(false)
    const configs = generateConfigs()

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="mcp-install">
            <h3>🔌 Connect AI Tools via MCP</h3>
            <p className="mcp-description">
                Access this documentation directly from your AI coding assistant using the Model Context Protocol.
            </p>

            <div className="mcp-tabs">
                <button
                    className={activeTab === 'vscode' ? 'active' : ''}
                    onClick={() => setActiveTab('vscode')}
                >
                    VS Code
                </button>
                <button
                    className={activeTab === 'claude-code' ? 'active' : ''}
                    onClick={() => setActiveTab('claude-code')}
                >
                    Claude Code
                </button>
                <button
                    className={activeTab === 'claude-desktop' ? 'active' : ''}
                    onClick={() => setActiveTab('claude-desktop')}
                >
                    Claude Desktop
                </button>
            </div>

            <div className="mcp-content">
                {activeTab === 'vscode' && (
                    <div className="mcp-panel">
                        <p>Click to install in VS Code:</p>
                        <a
                            href={configs.vscode}
                            className="mcp-install-btn"
                        >
                            ⚡ Install in VS Code
                        </a>
                        <p className="mcp-note">
                            Opens VS Code and adds the Legali Docs MCP server.
                        </p>
                    </div>
                )}

                {activeTab === 'claude-code' && (
                    <div className="mcp-panel">
                        <p>Run this command in your terminal:</p>
                        <div className="mcp-code-block">
                            <code>{configs.claudeCode}</code>
                            <button onClick={() => copyToClipboard(configs.claudeCode)}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'claude-desktop' && (
                    <div className="mcp-panel">
                        <p>Add to <code>~/.claude/settings.json</code>:</p>
                        <div className="mcp-code-block">
                            <pre>{configs.claudeDesktop}</pre>
                            <button onClick={() => copyToClipboard(configs.claudeDesktop)}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="mcp-resources">
                <h4>Resource URIs</h4>
                <p>Use MCP resources to read docs and stored documents:</p>
                <div className="mcp-code-block">
                    <pre>{`docs://<path>
store://<path>
store://index?limit=100&offset=0`}</pre>
                </div>
                <p className="mcp-note">
                    You can also poll for updates with <code>resources/list_changed</code> using the same endpoint.
                </p>
            </div>

            <style>{`
                .mcp-install {
                    background: var(--bg-secondary, #1a1a2e);
                    border: 1px solid var(--border-color, #333);
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin: 1.5rem 0;
                }
                .mcp-install h3 {
                    margin: 0 0 0.5rem;
                    color: var(--text-primary, #fff);
                }
                .mcp-description {
                    color: var(--text-secondary, #aaa);
                    margin-bottom: 1rem;
                }
                .mcp-tabs {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                }
                .mcp-tabs button {
                    padding: 0.5rem 1rem;
                    border: 1px solid var(--border-color, #333);
                    background: transparent;
                    color: var(--text-secondary, #aaa);
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .mcp-tabs button:hover {
                    background: var(--bg-hover, #252540);
                }
                .mcp-tabs button.active {
                    background: var(--accent-color, #6366f1);
                    color: white;
                    border-color: var(--accent-color, #6366f1);
                }
                .mcp-panel p {
                    margin: 0.5rem 0;
                    color: var(--text-secondary, #aaa);
                }
                .mcp-install-btn {
                    display: inline-block;
                    padding: 0.75rem 1.5rem;
                    background: var(--accent-color, #6366f1);
                    color: white;
                    text-decoration: none;
                    border-radius: 8px;
                    font-weight: 500;
                    margin: 0.5rem 0;
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .mcp-install-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
                }
                .mcp-code-block {
                    background: var(--bg-code, #0d0d1a);
                    border: 1px solid var(--border-color, #333);
                    border-radius: 8px;
                    padding: 1rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                    overflow-x: auto;
                }
                .mcp-code-block code, .mcp-code-block pre {
                    margin: 0;
                    color: var(--text-code, #e2e8f0);
                    font-family: 'Fira Code', monospace;
                    font-size: 0.875rem;
                    white-space: pre-wrap;
                    word-break: break-all;
                }
                .mcp-code-block button {
                    padding: 0.375rem 0.75rem;
                    background: var(--bg-secondary, #1a1a2e);
                    border: 1px solid var(--border-color, #333);
                    color: var(--text-secondary, #aaa);
                    border-radius: 4px;
                    cursor: pointer;
                    white-space: nowrap;
                }
                .mcp-code-block button:hover {
                    background: var(--bg-hover, #252540);
                }
                .mcp-note {
                    font-size: 0.875rem;
                    color: var(--text-muted, #666);
                }
                .mcp-resources {
                    margin-top: 1.5rem;
                    padding-top: 1rem;
                    border-top: 1px solid var(--border-color, #333);
                }
                .mcp-resources h4 {
                    margin: 0 0 0.5rem;
                    color: var(--text-primary, #fff);
                }
            `}</style>
        </div>
    )
}
